<template>
  <b-container class="chat-box">
    <b-row>
      <b-col cols="12" lg="10" class="mx-auto">
        <div
          v-for="(msg, msgidx) in messages"
          :key="msgidx"
          class="chat-record mt-2"
        >
          <ChatMessageComponent
            :style="{
              '--main-color': mainColor,
              '--secondary-color': secondaryColor,
            }"
            :initMsg="msg"
            :key="msgidx"
            :msgidx="msgidx"
            :ref="`msgRef-${msg.name}`"
            :context="context"
            @select-card="handleCardClick"
            @increase="handleIncrease"
            @decrease="handleDecrease"
            @continue="continueChat"
            @scroll-to-bottom="scrollToBottom"
            @scroll-to-msg="scrollToMsg"
            @select-checkbox="handleCheckbox"
            @set-text-input="setTextInput"
            @selector-change="handleSelectorChange"
            @go-to-step="goToStep"
          />
          <div>
            <b-row
              v-if="msg.isFinal"
              class="w-100 reset-button py-2 m-1"
              @click="resetChat"
            >
              <b-col>Reiniciar chat</b-col>
            </b-row>
          </div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
// Chatbot de Corrección de Tests (con sesión). Mismo esqueleto que los otros
// servicios —ChatMessageComponent + un grafo `questions`, menú de bienvenida
// con el saldo de usos y menú de regreso— y el corrector real montado como un
// paso `custom-component`.
//
// Antes esta vista traía su propio selector de profesión y de test (los pasos
// `role` / `test_list` / `test_correction` / `generate_report`, con los forms
// viejos). Todo eso lo hace hoy CorregirTestWorkspace, que además guarda la
// sesión y arma el informe combinado: los pasos duplicaban un flujo que ya
// existía mejor resuelto, y dejaban el menú del servicio sin las opciones que
// tienen el resto de los chatbots.
import ChatMessageComponent from "./ChatMessageComponent.vue";
import MissionsView from "@/components/utopie/miscellaneous/MissionsView";
import TestCorrectorStep from "./TestCorrectorStep.vue";
import informationService from "@/services/informationService";
import { MEETING_OPTION, meetingQuestion } from "./meetingOffer";
import { REFERRAL_OPTIONS, referralQuestions } from "./referralOffer";
import { ensureReferralRewards } from "./referralRewards";
import { usesLine, usesCardClass, spendsUses } from "./serviceUses";
import { skipAutoScroll } from "./chatScroll";
import {
  SERVICE_DETAILS_INTRO,
  SERVICE_DETAILS_STEPS,
  SERVICE_DETAILS_TESTS,
} from "@/components/corregir-test/constants/chatMessages";

const SERVICE = "tests";

// Las opciones del menú, en el orden que usa el resto de los servicios: primero
// la información, después la acción que gasta usos, y al final las formas de
// conseguir más (invitar primero, que reparte a los dos lados y trae gente
// nueva; después declarar quién te invitó, comprar, misiones y la reunión).
//
// El referido se lista siempre, como en Eureka, Planificaciones y Materiales.
// La variante que lo escondía cuando `check_referred` contesta con montos en
// cero (`referralOptions`) dejaba este menú sin las dos opciones, porque los
// montos de Corrección de Tests estan en cero en RedpieConfig.
const menuOptions = () => [
  {
    label: "Conocer más detalles",
    nextIndex: "details",
  },
  {
    label: "Corregir un test",
    nextIndex: "corrector",
  },
  // {
  //   label: "Recibir una cotización para mi colegio",
  //   nextIndex: "bill",
  // },
  ...REFERRAL_OPTIONS,
  {
    label: "Comprar usos",
    option: "redirect",
    redirect: "/utopie#/buy_product/tests",
  },
  {
    label: "Ver misiones y ganar usos",
    nextIndex: "missions",
  },
  MEETING_OPTION,
];

// Traduce una opción del menú a la tarjeta que espera ChatMessageComponent.
const toAnswer = (ans) => ({
  name: ans.label,
  isSelected: false,
  class: "col-md-7 col-12 my-2",
  bootstrap_classes: "text-left px-2 font-weight-bold",
  nextQuestionIndex: ans.nextIndex,
  redirect: ans.redirect,
  cardClass: usesCardClass(ans, SERVICE),
});

export default {
  components: { ChatMessageComponent },
  name: "ChatbotTestCorrection",
  data() {
    return {
      mainColor: "#b76629",
      secondaryColor: "#FFD6AF",
      currentQuestionIndex: "welcomeMessage",
      context: { service: "Corregir Test" },
      messages: [],
      types: ["text", "button", "quickReplies"],
      questions: {},
    };
  },
  created() {
    // Se pregunta acá, al montar, y no cuando la persona abre el referido: el
    // guard que decide si el referido se ofrece (`referralRewardsKnownEmpty`)
    // sólo se enteraba dentro de la vista de invitar, o sea después del clic
    // que queremos evitar. Es idempotente por servicio y falla abierto.
    ensureReferralRewards(SERVICE);
    this.questions = {
      welcomeMessage: {
        name: "welcomeMessage",
        vueContext: this,
        text() {
          return `👋 ¡Hola ${this.vueContext.$store.state.login.first_name}!
            Bienvenido a la Corrección de Tests del Decreto 170: incorporas los
            puntajes y descargas el informe en Word.<br/>
            ${usesLine(SERVICE)}<br/>
            ¿Qué te gustaría hacer?`;
        },
        hint: "Escoge una opción",
        type: "options",
        answersLoaded: false,
        single_choice: true,
        answers: [],
        nextQuestionIndex: "mainMessage",
        getAnswers() {
          this.answers = menuOptions().map(toAnswer);
          this.answersLoaded = true;
          return Promise.resolve(this.answers);
        },
        routine() {},
      },
      // Menú de regreso: una opción ya vista no reaparece (su paso quedó como
      // clave en `context`), salvo las que entregan usos, que siempre vuelven.
      mainMessage: {
        name: "mainMessage",
        text() {
          return `${usesLine(SERVICE)}<br/>
            Ahora, <strong>¿qué quieres hacer?</strong>`;
        },
        hint: "Escoge una opción",
        type: "options",
        answersLoaded: false,
        single_choice: true,
        isMain: true,
        answers: [],
        nextQuestionIndex: "mainMessage",
        getAnswers(context) {
          this.answers = menuOptions()
            .filter(
              // Ya usada = ya leída: no se vuelve a ofrecer. La única que
              // repite es corregir, que es la acción del servicio.
              (ans) => !(ans.nextIndex in context) || spendsUses(ans)
            )
            .map(toAnswer);
          this.answers.push({
            name: "Reiniciar chat",
            isSelected: false,
            class: "col-md-7 col-12 my-2",
            bootstrap_classes: "text-center px-2",
            nextQuestionIndex: "reset",
          });
          this.answersLoaded = true;
          return Promise.resolve(this.answers);
        },
        routine() {},
      },
      // El corrector real. Todo el flujo (profesión, test, puntajes, informe
      // combinado) vive dentro de CorregirTestWorkspace.
      corrector: {
        name: "corrector",
        text: "¡Perfecto! Elige la profesión y el test, incorpora los puntajes y descarga tu informe. Recuerda que cada corrección descuenta un uso, así que revisa bien los datos antes de corregir.",
        type: "custom-component",
        component: TestCorrectorStep,
        answersLoaded: true,
        answers: [],
        dontShowNextButton: true,
        nextQuestionIndex: "mainMessage",
        // Tener getAnswers evita que ChatMessageComponent emita `continue`
        // solo: el paso avanza cuando el corrector lo pide.
        getAnswers() {
          return Promise.resolve(this.answers);
        },
        routine() {},
      },
      details: {
        name: "details",
        text: "¡Perfecto! Aquí te muestro los detalles de la corrección automática de tests",
        type: "informative",
        nextQuestionIndex: "mainMessage",
        answersLoaded: false,
        answers: [],
        getAnswers() {
          if (this.answersLoaded) return Promise.resolve(this.answers);
          this.answers = [
            {
              title: "Corrección automática del Decreto 170",
              text: SERVICE_DETAILS_INTRO,
              class: "col-12 col-md-12 mb-2",
            },
            ...SERVICE_DETAILS_STEPS.map((step, idx) => ({
              title: `${idx + 1}. ${
                ["Sin cálculos a mano", "Informe en Word", "Informe unificado"][
                  idx
                ] || ""
              }`,
              text: step,
              class: "col-12 col-md-12 mb-2",
            })),
            {
              title: "Tests disponibles",
              text: SERVICE_DETAILS_TESTS,
              class: "col-12 col-md-12 mb-2",
            },
          ];
          this.answersLoaded = true;
          return Promise.resolve(this.answers);
        },
        routine() {},
      },
      bill: {
        name: "bill",
        text: "¡Excelente! Indícanos tu correo y la cantidad de correcciones a cotizar y te enviaremos la cotización.",
        type: "inputs",
        answersLoaded: true,
        answers: [
          {
            name: "email",
            type: "email",
            placeholder: "Escribe tu correo aquí",
            class: "col-12 col-md-6 mb-2",
          },
          {
            name: "applications",
            type: "number",
            placeholder: "Cantidad de correcciones",
            class: "col-12 col-md-6 mb-2",
          },
        ],
        nextMessage: "Obtener cotización!",
        extraFunction: (data) => {
          informationService
            .sendEurekaBill({
              email: data.email,
              applications: data.applications,
              service: "TEST",
            })
            .then((resp) => {
              this.$toasted.success(resp.message);
            });
        },
        nextQuestionIndex: "mainMessage",
        routine: () => {},
      },
      missions: {
        name: "missions",
        text: '<div class="text-left"><strong>¡Puedes conseguir usos gratis realizando las siguientes acciones!</strong></div>',
        type: "custom-component",
        component: MissionsView,
        answersLoaded: true,
        answers: [],
        dontShowNextButton: true,
        nextQuestionIndex: "mainMessage",
        routine: () => {},
      },
      meeting: meetingQuestion("mainMessage"),
      ...referralQuestions(this, "mainMessage", SERVICE),
    };
  },
  mounted() {
    // Corregir de verdad gasta un `tests_uses`, así que el corrector vive
    // detrás del login: quien llega sin sesión (un enlace viejo, un marcador)
    // se va a la demo, que es el producto entero pregrabado.
    if (!this.$store.state.login.token) {
      this.$router.replace({ name: "test-correction-demo" }).catch(() => {});
      return;
    }
    let question = this.questions[this.currentQuestionIndex];
    this.messages.push(question);
    this.context[question.name] = {};
    this.$store.commit("chatbot/clear");
  },
  computed: {
    currentQuestion() {
      return this.questions[this.currentQuestionIndex];
    },
  },
  methods: {
    // ChatMessageComponent emits (answer, msgidx, ...); `answer` is the same
    // object held in the message's answers array, so we mutate it directly.
    handleCardClick(answer, msgidx) {
      if (this.messages[msgidx].name !== this.currentQuestion.name) return;
      if (this.currentQuestion.single_choice) {
        this.messages[msgidx].answers.forEach((ans) => {
          ans.isSelected = false;
        });
      }
      answer.isSelected = !answer.isSelected;
      this.selectAnswer();
    },
    // Bound to ChatMessageComponent's @increase/@decrease (counter types);
    // this chatbot is options-only, so they are intentional no-ops.
    handleIncrease() {},
    handleDecrease() {},
    async continueChat() {
      switch (this.currentQuestion.type) {
        case "options":
          if (this.currentQuestion.single_choice) {
            this.context[this.currentQuestion.name] =
              this.currentQuestion.answers.find((ans) => ans.isSelected);
            this.$store.commit("chatbot/updateMessage", {
              name: this.currentQuestion.name,
              key: "answer",
              value: this.context[this.currentQuestion.name],
            });
            // Opciones que salen del chat (comprar usos). Mismo mecanismo que
            // Eureka y Documentos PIE: la respuesta lleva el destino y acá se
            // navega, sin paso intermedio en el grafo de preguntas.
            const chosen = this.context[this.currentQuestion.name];
            if (chosen && chosen.redirect) {
              window.location.href = chosen.redirect;
              return;
            }
          } else {
            this.context[this.currentQuestion.name] =
              this.currentQuestion.answers.filter((ans) => ans.isSelected);
            this.$store.commit("chatbot/updateMessage", {
              name: this.currentQuestion.name,
              key: "answers",
              value: this.context[this.currentQuestion.name],
            });
          }
          break;
        default:
          break;
      }

      this.messages.slice(-1)[0].answered = true;
      if (!this.currentQuestion.isFinal) {
        await this.currentQuestion.routine();
        if (!this.currentQuestion.single_choice) {
          if (typeof this.currentQuestion.nextQuestionIndex === "function") {
            this.currentQuestionIndex = this.currentQuestion.nextQuestionIndex(
              this.context
            );
          } else {
            this.currentQuestionIndex = this.currentQuestion.nextQuestionIndex;
          }
        } else {
          this.currentQuestionIndex =
            this.context[this.currentQuestion.name] &&
            this.context[this.currentQuestion.name].nextQuestionIndex
              ? this.context[this.currentQuestion.name].nextQuestionIndex
              : this.currentQuestion.nextQuestionIndex;
        }
        if (this.currentQuestionIndex == "reset") {
          this.resetChat();
        } else {
          this.loadQuestion(this.currentQuestionIndex);
        }
        this.context["currentQuestionIndex"] = this.currentQuestionIndex;
        this.$store.commit("chatbot/updateMessage", {
          name: "currentQuestionIndex",
          key: "value",
          value: this.currentQuestionIndex,
        });
      }
    },
    loadQuestion(questionIndex) {
      try {
        let question = this.questions[questionIndex];
        this.context[question.name] = {};
        this.messages.push(question);
      } catch (error) {
        console.log(error);
      }
    },
    goToStep(stepName) {
      // Custom steps can request a full chat reset via go-to-step("reset").
      if (stepName === "reset") {
        this.resetChat();
        return;
      }
      const question = this.questions[stepName];
      if (!question) return;
      this.messages.slice(-1)[0].answered = true;
      question.answered = false;
      this.currentQuestionIndex = stepName;
      this.loadQuestion(stepName);
    },
    selectAnswer() {
      if (this.currentQuestion.single_choice) {
        this.continueChat();
      }
    },
    scrollToBottom() {
      let container = document.querySelector(".global-container");
      if (container) window.scrollTo(0, container.scrollHeight);
    },
    scrollToMsg(name) {
      if (skipAutoScroll(this.messages, name)) return;
      const msgRef = this.$refs[`msgRef-${name}`];
      if (!msgRef || !msgRef.length) return;
      // El último, no el primero: el menú se vuelve a montar en cada vuelta y
      // `$refs` los acumula, así que `[0]` mandaba el scroll al menú de arriba
      // de todo en vez de al que se acaba de agregar.
      const parentMsg = msgRef[msgRef.length - 1].$el.parentNode;
      parentMsg.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    },
    resetChat() {
      this.$emit("toggle-reload");
    },
    handleCheckbox(msgidx, value) {
      this.messages[msgidx].optionalCheckbox = value;
      this.context[this.currentQuestion.name].optionalCheckbox = value;
    },
    setTextInput(msgidx, value) {
      this.messages[msgidx].textInput = value;
      this.context[this.currentQuestion.name].textInput = value;
    },
    handleSelectorChange(msgidx, idx, value) {
      this.messages[msgidx].answers[idx].selected = value;
      this.context[this.currentQuestion.name].answers =
        this.messages[msgidx].answers;
    },
  },
};
</script>

<style scoped>
.chat-box {
  margin: 10px;
  width: 100vw;
  margin-left: auto;
  margin-right: auto;
  align-items: space-between;
  justify-content: space-between;
}
.reset-button {
  border-radius: 5px;
  border: 2pt solid rgb(0, 0, 0) !important;
  font-family: "Ubuntu Medium", Arial, sans-serif;
  font-weight: bold;
  cursor: pointer;
}
</style>
